/**
 * Table body for the Current-Week drilldown modal.
 *
 * Extracted from ``CurrentWeekDrilldownModal`` to keep that file under the
 * 500-line limit. Renders either the GRN-keyed or schedule-keyed column set
 * depending on ``entity``. Both column sets surface the TWO dates that drive
 * the verdict so the user can see exactly what was compared:
 *   - GRN rows:      Receipt Date  vs  Sched Date  → on-time status
 *   - Schedule rows: Schedule Date  +  Promise Date → OTIF status
 */
import { ArrowUp, ArrowDown, ArrowUpDown, ShieldCheck, Wrench } from 'lucide-react';
import { OTIF_WINDOW_UNAVAILABLE_TEXT } from './OtifWindowUnavailableNote';
import PoLineLink from '../../common/PoLineLink';
import PoHeaderLink from '../../common/PoHeaderLink';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import type { POLineSchedule, GoodsReceiptNote } from '../../../lib/api';
import ManualAgreementBadge from '../../purchasing/ManualAgreementBadge';
import { useFormatters } from '../../../contexts/PreferencesContext';
import type { DrilldownEntity } from './currentWeekDrilldownParams';
import { SUPPLIER_PROMISE_DATE_SHORT } from '../../../config/dateLabels';

/** Coloured pill for the on-time status column (receipt vs schedule date). */
function OnTimeStatusPill({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  const map: Record<string, { label: string; cls: string }> = {
    early:   { label: 'Early',   cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    on_time: { label: 'On time', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    late:    { label: 'Late',    cls: 'bg-red-50 text-red-700 border-red-200' },
    // CR-6518: native overridden verdicts — green because they score as a
    // pass, labelled with the verdict they replaced.
    overridden_early: { label: 'Overridden (Early)', cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
    overridden_late:  { label: 'Overridden (Late)',  cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  };
  const m = map[value] ?? { label: value, cls: 'bg-slate-50 text-slate-600 border-slate-200' };
  return <Badge variant="outline" className={`${m.cls} whitespace-nowrap`}>{m.label}</Badge>;
}

/** Coloured pill for the per-schedule OTIF verdict column.
 *
 * Exported so the Per Week and YTD drilldowns render the verdict identically —
 * three tabs showing the same schedule must not disagree about how it looks.
 */
export function OtifStatusPill({ value, overridden, overrideReason, note }: { value: string | null | undefined; overridden?: boolean; overrideReason?: string; note?: string | null }) {
  if (overridden) {
    return (
      <Badge
        variant="outline"
        className="bg-emerald-50 text-emerald-700 border-emerald-300 whitespace-nowrap"
        title={overrideReason ? `Manual override: ${overrideReason}` : 'Manual OTIF override'}
      >
        <ShieldCheck className="h-3 w-3 mr-1" />Passed (Override)
      </Badge>
    );
  }
  if (!value && note === 'window_unavailable') {
    // CR-7714: not judged, and why — never a verdict on a substitute window.
    return (
      <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 whitespace-nowrap"
        title={OTIF_WINDOW_UNAVAILABLE_TEXT}>
        Not judged
      </Badge>
    );
  }
  if (!value) return <span className="text-muted-foreground">—</span>;
  const map: Record<string, { label: string; cls: string }> = {
    passed:  { label: 'Passed',  cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    pending: { label: 'Pending', cls: 'bg-slate-50 text-slate-600 border-slate-200' },
    failed:  { label: 'Failed',  cls: 'bg-red-100 text-red-900 border-red-300' },
  };
  const m = map[value] ?? { label: value, cls: 'bg-slate-50 text-slate-600 border-slate-200' };
  return <Badge variant="outline" className={`${m.cls} whitespace-nowrap`}>{m.label}</Badge>;
}

/** Mute zero numeric values so the eye lands on the non-zero rows. */
function NumCell({ value }: { value: number | string | undefined | null }) {
  const n = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  if (!n) return <span className="text-muted-foreground">0</span>;
  return <span className="tabular-nums">{n.toLocaleString(undefined, { maximumFractionDigits: 3 })}</span>;
}

export interface ColumnDef {
  id: string;
  label: string;
  align: 'left' | 'right';
  hint?: string;
}

export const SCHEDULE_COLUMNS: ColumnDef[] = [
  { id: 'schedule_date',        label: 'Schedule Date',  align: 'left',  hint: 'Date this slot was due — the primary date the OTIF verdict is judged against' },
  { id: 'received_date',        label: 'Received Date',  align: 'left',  hint: 'Actual delivery date (latest GRN receipt) — a schedule fails OTIF when this is outside the tolerance window even if fully received' },
  { id: 'supplier_promise_date', label: SUPPLIER_PROMISE_DATE_SHORT,  align: 'left',  hint: 'Supplier-committed delivery date for this slot' },
  { id: 'po_number',            label: 'PO #',           align: 'left' },
  { id: 'line_number',          label: 'Line',           align: 'right' },
  { id: 'schedule_line_number', label: 'Sched #',        align: 'right', hint: 'Schedule-line index within the PO line' },
  { id: 'part_number',          label: 'Part',           align: 'left' },
  { id: 'part_description',     label: 'Part Description', align: 'left' },
  { id: 'supplier_name',        label: 'Supplier',       align: 'left' },
  { id: 'schedule_qty',         label: 'Sched Qty',      align: 'right', hint: 'Quantity scheduled for delivery on this date' },
  { id: 'shipped_qty',          label: 'Received Qty',   align: 'right', hint: 'Quantity received against this schedule (sum of GRN allocations)' },
  { id: 'balance_qty',          label: 'Open Qty',       align: 'right', hint: 'Outstanding qty = Sched Qty − Received Qty' },
  { id: 'otif_status',          label: 'OTIF Status',    align: 'left',  hint: 'Per-schedule OTIF verdict: Passed / Pending / Failed' },
];

/** The ERP's own open qty for the slot, from its latest push. Shown to
 *  customer-side users on a tenant whose import captures it (DE); the real
 *  arrear is a past slot the ERP still reports open, so this sits beside
 *  Helix's Open Qty and the two are compared. */
export const ERP_BALANCE_COLUMN: ColumnDef = {
  id: 'erp_balance_qty', label: 'ERP Balance', align: 'right',
  hint: "Open qty the ERP reports for this slot in its latest push — the real arrear. Red when it differs from Helix's Open Qty",
};

/** Schedule column set, with the ERP Balance column beside Open Qty when wanted. */
export function scheduleColumns(showErpBalance: boolean): ColumnDef[] {
  if (!showErpBalance) return SCHEDULE_COLUMNS;
  const at = SCHEDULE_COLUMNS.findIndex((c) => c.id === 'balance_qty') + 1;
  return [...SCHEDULE_COLUMNS.slice(0, at), ERP_BALANCE_COLUMN, ...SCHEDULE_COLUMNS.slice(at)];
}

/** Helix and the ERP disagree on the slot's open qty (same rule as the ERP repair board). */
export function hasErpMismatch(r: POLineSchedule): boolean {
  return r.erp_balance_qty != null && r.status !== 'cancelled'
    && parseFloat(r.balance_qty) !== parseFloat(r.erp_balance_qty);
}

export const GRN_COLUMNS: ColumnDef[] = [
  { id: 'receipt_date',         label: 'Receipt Date',   align: 'left',  hint: 'Date the goods were received (GRN) — compared against the schedule date to set the verdict' },
  { id: 'schedule_date',        label: 'Sched Date',     align: 'left',  hint: 'Date this schedule slot was due — receipt date vs. this drives the on-time verdict' },
  { id: 'po_number',            label: 'PO #',           align: 'left' },
  { id: 'line_number',          label: 'Line',           align: 'right' },
  { id: 'schedule_line_number', label: 'Sched #',        align: 'right', hint: 'Schedule line this GRN was attributed to' },
  { id: 'part_number',          label: 'Part',           align: 'left' },
  { id: 'part_description',     label: 'Part Description', align: 'left' },
  { id: 'supplier_name',        label: 'Supplier',       align: 'left' },
  { id: 'schedule_qty',         label: 'Sched Qty',      align: 'right', hint: 'Quantity scheduled for delivery on this date (per schedule slot)' },
  { id: 'quantity_received',    label: 'Received Qty',   align: 'right', hint: 'Quantity received on this GRN' },
  { id: 'on_time_status',       label: 'On-time Status', align: 'left',  hint: 'Receipt vs schedule date verdict (Early / On time / Late)' },
];

export const SCHEDULE_SORT_FIELDS: Record<string, string | null> = {
  schedule_date: 'schedule_date',
  received_date: null,
  supplier_promise_date: 'supplier_promise_date',
  po_number: 'po_line__po__po_number',
  line_number: 'po_line__line_number',
  schedule_line_number: 'schedule_line_number_int',
  part_number: 'po_line__part_number',
  part_description: null,
  supplier_name: 'po_line__po__supplier__name',
  schedule_qty: 'schedule_qty',
  shipped_qty: 'shipped_qty',
  balance_qty: 'balance_qty',
  erp_balance_qty: 'erp_balance_qty',
  otif_status: null,
};

export const GRN_SORT_FIELDS: Record<string, string | null> = {
  receipt_date: 'receipt_date',
  schedule_date: 'po_line_schedule__schedule_date',
  po_number: 'po_line__po__po_number',
  line_number: 'po_line__line_number',
  schedule_line_number: null,
  part_number: 'po_line__part_number',
  part_description: null,
  supplier_name: 'po_line__po__supplier__name',
  schedule_qty: 'po_line_schedule__schedule_qty',
  quantity_received: 'quantity_received',
  on_time_status: 'on_time_status',
};

type AnyRow = POLineSchedule | GoodsReceiptNote;

interface TableProps {
  entity: DrilldownEntity;
  rows: AnyRow[];
  sortCol: string;
  sortDir: 'asc' | 'desc';
  onHeaderClick: (colId: string) => void;
  /** Staff/superuser OTIF override — when provided, schedule rows grow an
   *  override action column (drilldown modal gates on the user's flags). */
  onOtifOverride?: (row: POLineSchedule) => void;
  /** Show the ERP Balance column on schedule rows (customer-side, ERP-balance tenants). */
  showErpBalance?: boolean;
  /** Staff/superuser repair — when provided, schedule rows whose Helix balance
   *  differs from the ERP's grow a Repair button opening the GRN repair board. */
  onRepair?: (row: POLineSchedule) => void;
}

export function CurrentWeekDrilldownTable({
  entity, rows, sortCol, sortDir, onHeaderClick, onOtifOverride, showErpBalance = false, onRepair,
}: TableProps) {
  const { formatDate } = useFormatters();
  const showOverrideCol = entity !== 'grn' && !!onOtifOverride;
  const showRepairCol = entity !== 'grn' && !!onRepair;
  const columns = entity === 'grn' ? GRN_COLUMNS : scheduleColumns(showErpBalance);
  const sortFields = entity === 'grn' ? GRN_SORT_FIELDS : SCHEDULE_SORT_FIELDS;

  const sortIcon = (colId: string) => {
    if (!sortFields[colId]) return null;
    if (sortCol !== colId) return <ArrowUpDown className="h-3 w-3 text-muted-foreground inline ml-1" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 text-primary inline ml-1" />
      : <ArrowDown className="h-3 w-3 text-primary inline ml-1" />;
  };

  return (
    <div className="overflow-auto max-h-[60vh] border rounded-md">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 sticky top-0 z-10">
          <tr>
            {columns.map((c) => {
              const sortable = sortFields[c.id] !== null && sortFields[c.id] !== undefined;
              const align = c.align === 'right' ? 'text-right' : 'text-left';
              const cursor = sortable ? 'cursor-pointer hover:bg-slate-100' : '';
              return (
                <th
                  key={c.id}
                  title={c.hint}
                  className={`px-3 py-2 font-medium text-slate-700 select-none whitespace-nowrap ${align} ${cursor}`}
                  onClick={() => onHeaderClick(c.id)}
                  aria-sort={sortable && sortCol === c.id ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {c.label}{sortIcon(c.id)}
                </th>
              );
            })}
            {showOverrideCol && (
              <th className="px-3 py-2 font-medium text-slate-700 text-left whitespace-nowrap" title="Staff: manually override the OTIF verdict">
                Override
              </th>
            )}
            {showRepairCol && (
              <th className="px-3 py-2 font-medium text-slate-700 text-left whitespace-nowrap" title="Staff: match Helix's receipts to the ERP where the balances differ">
                Repair
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {entity === 'grn'
            ? (rows as GoodsReceiptNote[]).map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.receipt_date)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.schedule_date ? formatDate(r.schedule_date) : '—'}</td>
                  {/* CR-1154: PO number links to the PO header; line to the line item. */}
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                    <PoHeaderLink poLineId={r.po_line} poNumber={r.po_number}>{r.po_number ?? '—'}</PoHeaderLink>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <PoLineLink poLineId={r.po_line}><NumCell value={r.line_number} /></PoLineLink>
                  </td>
                  <td className="px-3 py-2 text-right text-xs text-muted-foreground whitespace-nowrap">{r.schedule_line_number ?? '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.part_number ?? '—'}</td>
                  <td className="px-3 py-2 text-xs max-w-[280px] truncate" title={r.part_description || ''}>{r.part_description || '—'}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground max-w-[180px] truncate" title={r.supplier_name || ''}>{r.supplier_name || '—'}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap"><NumCell value={r.schedule_qty} /></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap"><NumCell value={r.quantity_received} /></td>
                  <td className="px-3 py-2 whitespace-nowrap"><OnTimeStatusPill value={r.on_time_status as string | null} /></td>
                </tr>
              ))
            : (rows as POLineSchedule[]).map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.schedule_date)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.received_date ? formatDate(r.received_date) : '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.supplier_promise_date ? formatDate(r.supplier_promise_date) : '—'}</td>
                  {/* CR-1154: PO number links to the PO header; line to the line item. */}
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                    <PoHeaderLink poLineId={r.po_line_id} poNumber={r.po_number}>{r.po_number}</PoHeaderLink>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <PoLineLink poLineId={r.po_line_id}><NumCell value={r.line_number} /></PoLineLink>
                  </td>
                  <td className="px-3 py-2 text-right text-xs text-muted-foreground whitespace-nowrap">{r.schedule_line_number ?? '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.part_number}</td>
                  <td className="px-3 py-2 text-xs max-w-[280px] truncate" title={r.part_description || ''}>{r.part_description || '—'}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground max-w-[180px] truncate" title={r.supplier_name || ''}>{r.supplier_name || '—'}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap"><NumCell value={r.schedule_qty} /></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap"><NumCell value={r.shipped_qty} /></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap"><NumCell value={r.balance_qty} /></td>
                  {showErpBalance && (
                    <td className={`px-3 py-2 text-right whitespace-nowrap ${hasErpMismatch(r) ? 'text-red-700 font-semibold' : ''}`}
                      title={r.erp_balance_qty == null ? 'The ERP has not reported this slot' : hasErpMismatch(r) ? "Differs from Helix's Open Qty" : "Matches Helix's Open Qty"}>
                      {r.erp_balance_qty == null ? <span className="text-muted-foreground">—</span> : <NumCell value={r.erp_balance_qty} />}
                    </td>
                  )}
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <OtifStatusPill value={r.otif_status} overridden={r.otif_overridden} overrideReason={r.otif_override_reason} note={r.otif_status_note} />
                      {/* Manual Agreement: the line carries an agreed change the ERP
                          has not sent back, and auto-close skips it — why it is
                          still sitting in this tile. Closed from Scheduled Lines. */}
                      <ManualAgreementBadge schedule={r} compact />
                    </div>
                  </td>
                  {showOverrideCol && (
                    <td className="px-3 py-2 whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="outline"
                        className={r.otif_overridden
                          ? 'h-7 px-2 text-xs border-amber-300 text-amber-700 hover:bg-amber-50'
                          : 'h-7 px-2 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50'}
                        onClick={() => onOtifOverride?.(r)}
                        title={r.otif_overridden ? 'View / remove the manual OTIF override' : 'Mark this schedule OTIF-passed with a reason'}
                        type="button"
                      >
                        <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                        {r.otif_overridden ? 'Manage' : 'Override'}
                      </Button>
                    </td>
                  )}
                  {showRepairCol && (
                    <td className="px-3 py-2 whitespace-nowrap">
                      {hasErpMismatch(r) ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs border-red-300 text-red-700 hover:bg-red-50"
                          onClick={() => onRepair?.(r)}
                          title="Helix's open qty differs from the ERP's — open the GRN repair board for this line"
                          type="button"
                        >
                          <Wrench className="h-3.5 w-3.5 mr-1" />
                          Repair
                        </Button>
                      ) : (
                        <span className="text-muted-foreground" title="Helix matches the ERP for this slot">—</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}
